import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { creditos, fechaHora, gramos } from "../lib/formato";
import { Aviso, Cabecera, Cargando, Panel as Caja, TarjetaDato, Vacio } from "../componentes/piezas";
import GraficoIngresos from "../componentes/GraficoIngresos";
import PrimerosPasos from "../componentes/PrimerosPasos";
import InicioDelLocal from "../componentes/InicioDelLocal";
import Tablon from "../componentes/Tablon";
import { useSesion } from "../lib/sesion";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

interface DatosPanel {
  local?: { dentroAhora: number; entradasHoy: number; aforo: number };
  hoy: { dispensaciones: number; sociosAtendidos: number; totalCentimos: number; totalMg: number };
  barraHoy: { ventas: number; totalCentimos: number };
  mes: { dispensaciones: number; totalCentimos: number; totalMg: number };
  socios: { total: number; activos: number; porEstado: Record<string, number> };
  alertasStock: { id: string; nombre: string; stockMg: number; stockMinMg: number }[];
  cajaAbierta: { id: string; aperturaFecha: string; saldoInicialCentimos: number } | null;
  solicitudesPendientes: number;
  topDispensado: { nombre: string; entregas: number; cantidadMg: number; totalCentimos: number }[];
  topBarra: { nombre: string; cobros: number; unidades: number; totalCentimos: number }[];
  ultimasDispensaciones: {
    id: string;
    numero: number;
    fecha: string;
    totalCentimos: number;
    totalMg: number;
    estado: string;
    socio: { numeroSocio: number; nombre: string; apellidos: string };
  }[];
}

export default function Panel() {
  const { t, cuenta } = useIdioma();
  const { usuario, puede } = useSesion();
  const { data, isLoading } = useQuery({
    queryKey: ["panel"],
    queryFn: () => api<DatosPanel>("/informes/panel"),
    refetchInterval: 60_000,
  });

  const renovaciones = useQuery({
    queryKey: ["cuotas-pendientes", 30],
    queryFn: () => api<{ vencidas: unknown[]; porVencer: unknown[] }>("/cuotas/pendientes", { query: { dias: 30 } }),
    enabled: puede("cobrarCuota"),
  });

  if (isLoading || !data) return <Cargando />;

  const hora = new Date().getHours();
  const saludo = hora < 14 ? "p.buenosDias" : hora < 21 ? "p.buenasTardes" : "p.buenasNoches";
  const entregas = (n: number) => cuenta(n, "p.unaEntrega", "p.entregas");

  return (
    <>
      <Cabecera
        titulo={t("p.saludo", { saludo: t(saludo), nombre: usuario?.nombre.split(" ")[0] ?? "" })}
        descripcion={t("p.descripcion")}
      >
        {puede("dispensar") && (
          <Link className="boton" to="/mostrador">
            {t("p.nuevaDispensacion")}
          </Link>
        )}
      </Cabecera>

      <PrimerosPasos />

      {/*
        Lo primero de la pantalla es lo que se hace cien veces al dia, no las
        cifras. El dueño de un club de tres personas tambien atiende, asi que
        los botones los ve todo el mundo; lo que cambia debajo es lo demas,
        que ya va por permisos: el trabajador no ve el grafico ni los informes.
      */}
      <InicioDelLocal local={data.local} solicitudesPendientes={data.solicitudesPendientes} />

      <Tablon />

      {/* El aviso de la caja solo tiene sentido si hay algo que cobrar. A un
          club que acaba de entrar y no tiene ni socios se le estaba riñendo
          por no abrir una caja que todavía no le hace ninguna falta. */}
      {!data.cajaAbierta && puede("gestionarCaja") && data.socios.total > 0 && (
        <Aviso tipo="atencion">
          {t("p.sinCaja")} <Link to="/caja">{t("p.abrirCaja")}</Link>
        </Aviso>
      )}

      {data.solicitudesPendientes > 0 && puede("gestionarSolicitudes") && (
        <Aviso tipo="info">
          {cuenta(data.solicitudesPendientes, "p.unaSolicitud", "p.solicitudes")}{" "}
          <Link to="/comunidad">{t("p.verSolicitudes")}</Link>
        </Aviso>
      )}

      {renovaciones.data && (renovaciones.data.vencidas.length > 0 || renovaciones.data.porVencer.length > 0) && (
        <Aviso tipo={renovaciones.data.vencidas.length > 0 ? "error" : "atencion"}>
          {renovaciones.data.vencidas.length > 0 &&
            `${cuenta(renovaciones.data.vencidas.length, "p.unaCuotaVencida", "p.cuotasVencidas")} `}
          {renovaciones.data.porVencer.length > 0 &&
            `${cuenta(renovaciones.data.porVencer.length, "p.unaPorVencer", "p.porVencer")} `}
          <Link to="/renovaciones">{t("p.verRenovaciones")}</Link>
        </Aviso>
      )}

      {data.alertasStock.length > 0 && (
        <Aviso tipo="atencion">
          {data.alertasStock.length === 1
            ? t("p.pocoStockDe", { producto: data.alertasStock[0]!.nombre })
            : t("p.bajoMinimo", { n: data.alertasStock.length })}{" "}
          <Link to="/inventario">{t("p.verInventario")}</Link>
        </Aviso>
      )}

      <div className="rejilla cuatro" style={{ marginBottom: 16 }}>
        <TarjetaDato
          etiqueta={t("p.dispensadoHoy")}
          valor={gramos(data.hoy.totalMg)}
          pie={t("p.entregasASocios", {
            entregas: entregas(data.hoy.dispensaciones),
            socios: cuenta(data.hoy.sociosAtendidos, "p.unSocio", "p.socios"),
          })}
        />
        <TarjetaDato
          etiqueta={t("p.aportacionesHoy")}
          valor={creditos(data.hoy.totalCentimos)}
          pie={data.cajaAbierta ? t("p.cajaAbierta") : t("p.sinCajaAbierta")}
        />
        <TarjetaDato
          etiqueta={t("p.barraHoy")}
          valor={creditos(data.barraHoy.totalCentimos)}
          pie={cuenta(data.barraHoy.ventas, "ba.unCobro", "ba.cobros")}
        />
        <TarjetaDato
          etiqueta={t("p.sociosActivos")}
          valor={data.socios.activos}
          pie={t("p.enTotal", { n: data.socios.total })}
        />
        <TarjetaDato
          etiqueta={t("p.esteMes")}
          valor={gramos(data.mes.totalMg, 0)}
          pie={t("p.enEntregas", {
            importe: creditos(data.mes.totalCentimos),
            entregas: entregas(data.mes.dispensaciones),
          })}
        />
      </div>

      {puede("verInformes") && (
        <div style={{ marginBottom: 16 }}>
          <GraficoIngresos />
        </div>
      )}

      {/* Lo que mas ha salido hoy. Es la pregunta que se hace cualquiera que
          entra por la mañana, y hasta ahora habia que ir a los informes. */}
      <div className="rejilla dos" style={{ marginBottom: 16 }}>
        <Caja titulo={t("p.masDispensado")} sinCuerpo>
          {data.topDispensado.length === 0 ? (
            <Vacio>{t("p.nadaHoy")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("p.producto")}</th>
                    <th className="num">{t("p.entregasColumna")}</th>
                    <th className="num">{t("p.cantidad")}</th>
                    <th className="num">{t("p.importe")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topDispensado.map((p) => (
                    <tr key={p.nombre}>
                      <td>{p.nombre}</td>
                      <td className="num tabular">{p.entregas}</td>
                      <td className="num tabular">{gramos(p.cantidadMg)}</td>
                      <td className="num tabular">{creditos(p.totalCentimos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Caja>

        <Caja titulo={t("p.masBarra")} sinCuerpo>
          {data.topBarra.length === 0 ? (
            <Vacio>{t("p.nadaBarra")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("p.producto")}</th>
                    <th className="num">{t("p.cobrosColumna")}</th>
                    <th className="num">{t("p.unidades")}</th>
                    <th className="num">{t("p.importe")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.topBarra.map((p) => (
                    <tr key={p.nombre}>
                      <td>{p.nombre}</td>
                      <td className="num tabular">{p.cobros}</td>
                      <td className="num tabular">{p.unidades}</td>
                      <td className="num tabular">{creditos(p.totalCentimos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Caja>
      </div>

      <div className="rejilla dos">
        <Caja titulo={t("p.ultimas")} sinCuerpo>
          {data.ultimasDispensaciones.length === 0 ? (
            <Vacio>{t("p.sinDispensaciones")}</Vacio>
          ) : (
            <div className="tabla-envoltorio">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>{t("p.socio")}</th>
                    <th>{t("p.fecha")}</th>
                    <th className="num">{t("p.cantidad")}</th>
                    <th className="num">{t("p.importe")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ultimasDispensaciones.map((d) => (
                    <tr key={d.id}>
                      <td>
                        #{d.socio.numeroSocio} {d.socio.nombre} {d.socio.apellidos}
                        {d.estado === "ANULADA" && (
                          <span className="etiqueta roja" style={{ marginLeft: 6 }}>
                            {t("p.anulada")}
                          </span>
                        )}
                      </td>
                      <td className="suave pequeno">{fechaHora(d.fecha)}</td>
                      <td className="num tabular">{gramos(d.totalMg)}</td>
                      <td className="num tabular">{creditos(d.totalCentimos)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Caja>

        {data.socios.total === 0 ? (
          <Caja titulo={t("p.primerosPasos")}>
            <p className="suave" style={{ marginTop: 0 }}>
              {t("p.recienCreado")}
            </p>
            <ol className="columna" style={{ paddingLeft: 18, margin: 0, gap: 10 }}>
              <li>
                <Link to="/ajustes">{t("p.paso.ajustes")}</Link>
                {t("p.paso.ajustesResto")}
              </li>
              <li>
                <Link to="/catalogo">{t("p.paso.catalogo")}</Link>
                {t("p.paso.catalogoResto")}
              </li>
              <li>
                <Link to="/inventario">{t("p.paso.stock")}</Link>
                {t("p.paso.stockResto")}
              </li>
              <li>
                <Link to="/socios/nuevo">{t("p.paso.socio")}</Link>
                {t("p.paso.socioResto")}
              </li>
              <li>
                <Link to="/caja">{t("p.paso.caja")}</Link>
                {t("p.paso.cajaResto")}
              </li>
            </ol>
          </Caja>
        ) : (
        <Caja titulo={t("p.estadoSocios")}>
          <div className="columna">
            {Object.entries(data.socios.porEstado)
              .sort(([, a], [, b]) => b - a)
              .map(([estado, cuantos]) => (
                <div key={estado} className="fila separada">
                  <span>{t(`g.estado.${estado}` as Clave)}</span>
                  <strong className="tabular">{cuantos}</strong>
                </div>
              ))}
          </div>

          {data.alertasStock.length > 0 && (
            <>
              <h3 style={{ margin: "20px 0 8px" }}>{t("p.stockBajo")}</h3>
              <div className="columna">
                {data.alertasStock.slice(0, 6).map((p) => (
                  <div key={p.id} className="fila separada">
                    <span>{p.nombre}</span>
                    <span className="etiqueta roja tabular">{gramos(p.stockMg)}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </Caja>
        )}
      </div>
    </>
  );
}
